import { THEME } from '@Theme';
import { StyleSheet } from 'aphrodite';
import { brandPrimary, white } from '../../../web/styles/colors';
import { shadowSm } from '../../../web/styles/shadows';

const { spacing, colors, typography, common } = THEME;

export const styles = StyleSheet.create({
	surveyItemIcon: {
		width: 16,
		height: 16,
		color: brandPrimary,
	},
	conversation: {
		flex: 1,
		minHeight: 0,
	},
	stickyTopBar: {
		display: 'flex',
		justifyContent: 'space-between',
		padding: '12px 20px',
		paddingBottom: 0,
		paddingLeft: 0,
		alignItems: 'flex-end',
		borderBottom: `1px solid ${colors.border.light}`,
		background: white,
	},
	stickyTopBarTabs: {
		borderBottom: 'none',
	},
	stickyTopBarActions: {
		display: 'flex',
		gap: 8,
		paddingBottom: 12,
	},
	behalfCombo: {
		minWidth: 240,
	},
	impersonationBar: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		padding: '12px 20px',
		borderBottom: `1px solid ${colors.border.light}`,
		background: white,
	},
	impersonationLabel: {
		fontSize: typography.fontSizes.sm,
		fontWeight: typography.fontWeights.semibold,
		color: colors.text.light,
	},
	impersonationSelect: {
		// Wide enough to comfortably show an admin's name + email in the combobox trigger.
		minWidth: 240,
	},
	outerContainer: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
	},
	twoColumnContainer: {
		display: 'grid',
		flex: 1,
		minHeight: 0,
		gridTemplateColumns: 'minmax(0, 1024px) minmax(0, 768px)',
		justifyContent: 'center',
		boxSizing: 'border-box',
	},
	editorPanel: {
		overflowY: 'auto',
		padding: spacing.lg,
		gap: 24,
		display: 'flex',
		flexDirection: 'column',
		...common.scrollBar,
	},
	previewPanel: {
		display: 'flex',
		flexDirection: 'column',
		overflowY: 'auto',
		backgroundColor: colors.background.main,
		borderLeft: `1px solid ${colors.border.light}`,
		...common.scrollBar,
	},
	container: {
		padding: spacing.lg,
		maxWidth: 800,
	},
	leftTabRoot: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		overflow: 'auto',
	},
	leftTabContent: {
		':is([data-state="active"])': {
			display: 'flex',
			flexDirection: 'column',
			flex: 1,
			minHeight: 0,
		},
	},
	leftTabContentAIBuilder: {
		paddingBottom: 24,
		paddingRight: 24,
		paddingTop: 24,
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: spacing.lg,
	},
	title: {
		fontSize: typography.fontSizes.xl,
		fontWeight: 600,
		color: colors.text.main,
	},
	actions: {
		display: 'flex',
		gap: spacing.sm,
	},
	embedPopover: {
		padding: 16,
		width: 512,
		maxWidth: 'calc(100vw - 32px)',
	},
	embedPopoverLayout: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
	},
	embedOptionsContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	},
	embedOptionGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	embedOptionLabel: {
		fontSize: 12,
		fontWeight: 600,
		color: colors.text.light,
	},
	embedCopyButton: {
		alignSelf: 'flex-end',
	},
	embedSnippetInput: {
		flex: 1,
		fontFamily: 'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
		fontSize: typography.fontSizes.xs,
		height: 120,
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.lg,
		maxWidth: 600,
	},
	avatarUploaderContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: spacing.xs,
		marginBottom: spacing.md,
	},
	assistantImageLabel: {
		fontSize: typography.fontSizes.xs,
		color: colors.text.light,
	},
	publicAccessContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
	},
	publicAccessToggleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
	},
	publicAccessLabel: {
		fontSize: typography.fontSizes.sm,
		color: colors.text.light,
	},
	// Allowed origins styles for public access
	allowedOriginsContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
		alignItems: 'flex-start',
	},
	subLabel: {
		fontSize: typography.fontSizes.xs,
		fontWeight: 500,
		color: colors.text.light,
		marginBottom: spacing.xs,
	},
	allowedOriginRow: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		width: '100%',
	},
	allowedOriginInput: {
		flex: 1,
	},
	addAllowedOriginLink: {
		paddingLeft: 0,
		paddingRight: 0,
	},
	// Conversation starters form styles
	conversationStartersContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
		alignItems: 'flex-start',
	},
	conversationStarterRow: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		width: '100%',
	},
	conversationStarterInput: {
		flex: 1,
	},
	conversationStarterRemoveButton: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: spacing.xs,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		':hover': {
			opacity: 0.7,
		},
	},
	addConversationStarterLink: {
		paddingLeft: 0,
		paddingRight: 0,
	},
	formGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	},
	label: {
		fontSize: typography.fontSizes.sm,
		fontWeight: 600,
		color: colors.text.main,
	},
	textArea: {
		minHeight: 120,
		backgroundColor: 'white',
		boxShadow: shadowSm,
		resize: 'vertical',
		':focus-visible': {
			border: `1px solid ${brandPrimary}`,
		},
	},
	instructionsArea: {
		minHeight: 240,
	},
	technicalPromptArea: {
		minHeight: 200,
	},
	objectivePromptArea: {
		minHeight: 150,
		resize: 'vertical',
	},
	selectorsContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
	},
	selectorRow: {
		display: 'flex',
		gap: spacing.sm,
		alignItems: 'center',
	},
	selectorInput: {
		flex: 1,
	},
	addSelectorButton: {
		alignSelf: 'flex-start',
	},
	errorMessage: {
		color: colors.danger.main,
		fontSize: typography.fontSizes.sm,
		marginTop: spacing.xs,
	},
	infoMessage: {
		color: colors.text.light,
		fontSize: typography.fontSizes.sm,
		marginTop: spacing.xs,
		fontStyle: 'italic',
	},
	variablesContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
	},
	variableRow: {
		display: 'flex',
		gap: spacing.sm,
		alignItems: 'center',
	},
	variableKeyInput: {
		flex: 1,
		maxWidth: 200,
	},
	variableValueInput: {
		flex: 2,
	},
	emptyVariables: {
		color: colors.text.light,
		fontSize: typography.fontSizes.sm,
		fontStyle: 'italic',
		padding: spacing.md,
		backgroundColor: colors.background.light,
		borderRadius: 4,
	},
	previewContainer: {
		marginTop: spacing.md,
		padding: spacing.md,
		backgroundColor: colors.background.light,
		borderRadius: 4,
		border: `1px solid ${colors.border.main}`,
	},
	previewHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: spacing.sm,
	},
	previewTitle: {
		fontSize: typography.fontSizes.sm,
		fontWeight: 600,
		color: colors.text.main,
	},
	previewContent: {
		fontSize: typography.fontSizes.sm,
		color: colors.text.main,
		whiteSpace: 'pre-wrap',
		fontFamily: 'monospace',
		maxHeight: 300,
		overflowY: 'auto',
	},
	highlightedVariable: {
		backgroundColor: colors.primary.light,
		padding: '2px 4px',
		borderRadius: 2,
	},
	// Survey capability section styles
	surveyCapabilityContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
		alignItems: 'flex-start',
	},
	surveyRow: {
		display: 'flex',
		gap: spacing.xs,
		width: '100%',
	},
	surveyRowHeader: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
		flex: 1,
	},
	surveyCombobox: {
		flex: 1,
	},
	surveyDescriptionInput: {
		minHeight: 60,
	},
	addSurveyLink: {
		paddingLeft: 0,
		paddingRight: 0,
	},
	teamMeetingItemMeta: {
		fontSize: typography.fontSizes.xs,
		color: colors.text.light,
		marginLeft: spacing.xs,
	},
	disabledTag: {
		fontSize: typography.fontSizes.xs,
		color: colors.text.light,
		border: `1px solid ${colors.border.main}`,
		borderRadius: common.borderRadius,
		padding: `0 ${spacing.xs}px`,
		marginLeft: 'auto',
	},
	warningMessage: {
		color: colors.warning.main,
		fontSize: typography.fontSizes.sm,
	},
	helpText: {
		fontSize: typography.fontSizes.xs,
		color: colors.text.light,
		marginTop: spacing.xs,
		marginBottom: spacing.sm,
	},
});
